import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useCollectionsStore } from '../store/collectionsStore';
import {
  setEnvironments,
  setSelectedEnvironment,
  setWorkspaceVariables,
  setGlobalVariables,
  setCollectionVariablesById,
} from '../store/variablesStore';
import { usesLocalDataPath } from '../utils/workspaceStorage';

/**
 * Hydrates variable scopes from workspace + collection payloads. Extracted
 * verbatim from App (same effects, same dep arrays, same call position) so
 * effect ordering is unchanged. The `lastHydratedWorkspaceIdRef` gate keeps the
 * workspace-scoped hydration firing only on an actual workspace switch, not on
 * every `currentWorkspace` object mutation from inline variable edits.
 */
export function useVariableHydration() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const collections = useCollectionsStore((s) => s.collections);

  const lastHydratedWorkspaceIdRef = useRef(null);

  useEffect(() => {
    if (!currentWorkspace) {
      lastHydratedWorkspaceIdRef.current = null;
      setWorkspaceVariables([]);
      setEnvironments([]);
      setSelectedEnvironment(null);
      return;
    }

    if (usesLocalDataPath(currentWorkspace, isAuthenticated)) {
      lastHydratedWorkspaceIdRef.current = currentWorkspace.id;
      return;
    }

    if (lastHydratedWorkspaceIdRef.current === currentWorkspace.id) return;
    lastHydratedWorkspaceIdRef.current = currentWorkspace.id;

    const workspaceScopes = currentWorkspace.variable_scopes
      || {
        globals: currentWorkspace.settings?.globals || [],
        workspace_globals: currentWorkspace.settings?.workspace_globals || [],
        environments: currentWorkspace.settings?.environments || []
      };

    const nextGlobals = workspaceScopes.globals || [];
    const nextWorkspaceVariables = workspaceScopes.workspace_globals || [];
    const nextEnvironments = workspaceScopes.environments || [];

    setGlobalVariables(nextGlobals);
    setWorkspaceVariables(nextWorkspaceVariables);
    setEnvironments(nextEnvironments);

    setSelectedEnvironment((prev) => {
      if (!prev) {
        return nextEnvironments[0] || null;
      }
      return nextEnvironments.find((env) => env.id === prev.id || env.name === prev.name) || null;
    });
  }, [currentWorkspace, isAuthenticated]);

  // Hydrate collection-scoped variables from collections payload.
  useEffect(() => {
    const nextMap = {};
    collections.forEach((collection) => {
      nextMap[collection.id] = collection.variable_scopes?.collection_variables
        || collection.settings?.collection_variables
        || [];
    });
    setCollectionVariablesById(nextMap);
  }, [collections]);
}
